import { useMemo } from "react";
import {
  Typography,
  Card,
  CardHeader,
  CardBody,
  Chip,
} from "@material-tailwind/react";
import { useCollection } from "react-firebase-hooks/firestore";
import { collection, orderBy, query, limit } from "firebase/firestore";
import db from "../firebase";

// Recent runs window — large enough to find the last success even after
// weeks of failures (scheduled runs are ~10/day). If no success appears in
// this window we show a "no recent success" fallback rather than a date.
const RUNS_WINDOW = 300;

const runsQuery = query(
  collection(db, "aircraftpost_scrape_runs"),
  orderBy("createdAt", "desc"),
  limit(RUNS_WINDOW)
);

const fmtDateTime = (d) =>
  d
    ? d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const daysAgo = (d) => {
  if (!d) return null;
  return Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
};

export function AircraftPostStatusCard() {
  const [snap, loading] = useCollection(runsQuery);

  const status = useMemo(() => {
    if (!snap || snap.empty) return null;

    const runs = snap.docs.map((d) => {
      const x = d.data();
      return {
        createdAt: x.createdAt?.toDate?.() || null,
        ok: x.status === "success",
        error: typeof x.error === "string" ? x.error : "",
        trigger: x.trigger,
      };
    });

    const latest = runs[0];
    let consecutiveFailures = 0;
    for (const r of runs) {
      if (r.ok) break;
      consecutiveFailures++;
    }
    const lastSuccess = runs.find((r) => r.ok) || null;

    let level = "ok"; // ok | warn | fail
    let label = "Healthy";
    if (!latest.ok) {
      level = "fail";
      label = "Failing";
    } else if (daysAgo(latest.createdAt) >= 2) {
      // Last run succeeded but nothing has run recently — scheduler problem
      level = "warn";
      label = "Not running";
    }

    return { latest, lastSuccess, consecutiveFailures, level, label };
  }, [snap]);

  const dotColor =
    status?.level === "ok"
      ? "bg-green-500"
      : status?.level === "warn"
        ? "bg-amber-500"
        : "bg-red-500";
  const chipColor =
    status?.level === "ok" ? "green" : status?.level === "warn" ? "amber" : "red";

  return (
    <Card>
      <CardHeader floated={false} shadow={false} color="transparent" className="m-0 p-4 pb-0">
        <div className="flex items-center gap-2">
          {status && <span className={`h-3 w-3 rounded-full ${dotColor}`} />}
          <Typography variant="h6" color="blue-gray">
            AircraftPost Scraping
          </Typography>
          {status && <Chip size="sm" value={status.label} color={chipColor} variant="ghost" />}
        </div>
      </CardHeader>
      <CardBody className="p-4 pt-2">
        {loading && (
          <Typography variant="small" color="gray">
            Loading scrape status…
          </Typography>
        )}
        {!loading && !status && (
          <Typography variant="small" color="gray">
            No scrape runs recorded.
          </Typography>
        )}
        {status && (
          <div className="space-y-1">
            <Typography variant="small" color="blue-gray">
              Last success:{" "}
              <span className="font-medium">
                {status.lastSuccess
                  ? `${fmtDateTime(status.lastSuccess.createdAt)} (${daysAgo(status.lastSuccess.createdAt)}d ago)`
                  : `none in last ${RUNS_WINDOW} runs`}
              </span>
            </Typography>
            <Typography variant="small" color="blue-gray">
              Last attempt:{" "}
              <span className="font-medium">{fmtDateTime(status.latest.createdAt)}</span>
              {status.latest.trigger ? ` (${status.latest.trigger})` : ""}
            </Typography>
            {status.consecutiveFailures > 0 && (
              <Typography variant="small" color="red">
                {status.consecutiveFailures >= RUNS_WINDOW ? `${RUNS_WINDOW}+` : status.consecutiveFailures}{" "}
                consecutive failures
              </Typography>
            )}
            {status.latest.error && (
              <Typography variant="small" color="gray" className="italic truncate" title={status.latest.error}>
                {status.latest.error}
              </Typography>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default AircraftPostStatusCard;
